import { useState, useEffect, useCallback, useMemo } from "react";
import { getAiLabQuantMessageWsUrl, getAiLabQuantMessageWsSubscribeBody } from "@/api/aicopy";

/** 解析 WS 下行 quant_strategy_status_item，取 strategyStatus 文案 */
export function parseQuantStrategyStatusWsItem(data) {
  if (!data || typeof data !== "object") return null;
  if (data.type !== "quant_strategy_status_item") return null;
  const strategyId = data.strategyId != null && data.strategyId !== "" ? String(data.strategyId) : null;
  const text = data.status?.strategyStatus;
  if (!strategyId || text == null || String(text).trim() === "") return null;
  return { strategyId, text: String(text).trim() };
}

/**
 * 当前跟单：按策略 ID 各建一条 Quant WS，订阅后接收 quant_strategy_status_item 并更新文案。
 * @param {boolean} enabled
 * @param {Array<string|number>} strategyIds
 * @param {string} locale
 */
export function useQuantStrategyStatusWs(enabled, strategyIds, locale) {
  const [statusByStrategyId, setStatusByStrategyId] = useState({});

  const stableIds = useMemo(() => {
    const set = new Set();
    for (const id of strategyIds || []) {
      if (id != null && id !== "") set.add(String(id));
    }
    return Array.from(set).sort();
  }, [strategyIds]);

  const stableIdsKey = stableIds.join(",");

  useEffect(() => {
    if (!enabled || stableIds.length === 0 || typeof WebSocket === "undefined") {
      return undefined;
    }

    setStatusByStrategyId((prev) => {
      const next = {};
      for (const id of stableIds) {
        if (prev[id]) next[id] = prev[id];
      }
      return next;
    });

    const connections = [];

    for (const strategyId of stableIds) {
      let ws;
      try {
        ws = new WebSocket(getAiLabQuantMessageWsUrl());
      } catch {
        continue;
      }

      const onOpen = () => {
        try {
          ws.send(JSON.stringify(getAiLabQuantMessageWsSubscribeBody(locale, { strategyId })));
        } catch {
          /* ignore */
        }
      };

      const onMessage = (event) => {
        let data;
        try {
          data = JSON.parse(event.data);
        } catch {
          return;
        }
        if (!data || typeof data !== "object") return;
        if (data.type === "subscribe_ack" || data.type === "heartbeat") return;
        const parsed = parseQuantStrategyStatusWsItem(data);
        if (!parsed) return;
        setStatusByStrategyId((prev) => ({
          ...prev,
          [parsed.strategyId]: parsed.text,
        }));
      };

      ws.addEventListener("open", onOpen);
      ws.addEventListener("message", onMessage);
      connections.push({ ws, onOpen, onMessage });
    }

    return () => {
      for (const { ws, onOpen, onMessage } of connections) {
        try {
          ws.removeEventListener("open", onOpen);
          ws.removeEventListener("message", onMessage);
          ws.close();
        } catch {
          /* ignore */
        }
      }
    };
  }, [enabled, locale, stableIdsKey]);

  const getStatusLabel = useCallback(
    (strategyId, fallback = "") => {
      if (strategyId == null || strategyId === "") return fallback;
      return statusByStrategyId[String(strategyId)] || fallback;
    },
    [statusByStrategyId],
  );

  return { statusByStrategyId, getStatusLabel };
}
